import React, { useState } from "react";
import { Link } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiChevronDown } from "@mdi/js";
import { FOOTER } from "./mock";

// A footer column. On desktop the heading and its links are always shown. On
// mobile (see v2.scss) the heading becomes a tappable accordion toggle and the
// body collapses — only when `collapsible` is set. Non-collapsible columns
// (Support, Payment, Follow) stay expanded on every breakpoint.
const FooterCol: React.FC<{
  title: string;
  collapsible?: boolean;
  className?: string;
  children: React.ReactNode;
}> = ({ title, collapsible = false, className = "", children }) => {
  const [open, setOpen] = useState(false);

  return (
    <div
      className={`gd-footer-v2__col ${collapsible ? "gd-footer-v2__col--accordion" : ""} ${className}`.trim()}
      data-open={open}
    >
      {collapsible ? (
        <button type="button" className="gd-footer-v2__col-head" onClick={() => setOpen((v) => !v)}>
          <h4>{title}</h4>
          <Icon className="gd-footer-v2__col-chevron" path={mdiChevronDown} size="20px" />
        </button>
      ) : (
        <h4>{title}</h4>
      )}
      <div className="gd-footer-v2__col-body">{children}</div>
    </div>
  );
};

export default function Footer() {
  return (
    <footer id="gd-footer-v2">
      <div className="gd-footer-v2__inner">
        <div className="gd-footer-v2__top">
          <div className="gd-footer-v2__brand">
            <img src="/iceholidays-assets/images/GD_logo.png" alt="Golden Destinations" />
            <p>Six brands built around six ways of seeing the world.</p>
          </div>

          <FooterCol title="Our Brands" collapsible>
            {FOOTER.brands.map((b) => (
              <span key={b} className="gd-footer-v2__link">{b}</span>
            ))}
          </FooterCol>

          <FooterCol title="Company" collapsible>
            {FOOTER.company.map((c) => (
              <Link key={c.label} to={c.href} className="gd-footer-v2__link">{c.label}</Link>
            ))}
          </FooterCol>

          <FooterCol title="Support" className="gd-footer-v2__col--support">
            {FOOTER.support.map((c) => (
              <Link key={c.label} to={c.href} className="gd-footer-v2__link">{c.label}</Link>
            ))}
          </FooterCol>

          <FooterCol title="Payment Method" className="gd-footer-v2__col--payment">
            <div className="gd-footer-v2__payments">
              {FOOTER.payments.map((p) => (
                <img key={p} src={p} alt="Payment method" />
              ))}
            </div>
          </FooterCol>
        </div>

        <div className="gd-footer-v2__band">
          <div className="gd-footer-v2__awards">
            <h4 className="gd-footer-v2__section-title">Recognised for Excellence</h4>
            <div className="gd-footer-v2__award-logos">
              {FOOTER.awards.map((a) => (
                <img key={a} src={a} alt="Award" />
              ))}
            </div>
          </div>
          <div className="gd-footer-v2__follow">
            <h4 className="gd-footer-v2__section-title">Follow Us</h4>
            <div className="gd-footer-v2__socials">
              {FOOTER.socials.map((s) => (
                <a key={s.icon} href={s.href} target="_blank" rel="noopener noreferrer">
                  <img src={s.icon} alt="Social link" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="gd-footer-v2__accreditations">
          {FOOTER.accreditations.map((a) => (
            <img key={a} src={a} alt="Accreditation" />
          ))}
        </div>

        <div className="gd-footer-v2__bottom">
          <span>Copyright © 2026 Golden Destinations. All rights reserved.</span>
          <div className="gd-footer-v2__bottom-links">
            <Link to="/web/footer/privacy">Privacy</Link>
            <Link to="/web/footer/terms">Terms</Link>
            <span>Cookie settings</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
